import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { Surcharge, SurchargeListResponse, SurchargeCreate, SurchargeUpdate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10;

interface UseSurchargesResult {
  surcharges: Surcharge[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchSurcharges: (page?: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  createSurcharge: (data: SurchargeCreate) => Promise<boolean>;
  updateSurcharge: (id: number, data: SurchargeUpdate) => Promise<boolean>;
  deleteSurcharge: (id: number) => Promise<boolean>;
}

export const useSurcharges = (): UseSurchargesResult => {
  const [surcharges, setSurcharges] = useState<Surcharge[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Removed hasFetchedInitial useRef

  const fetchSurcharges = useCallback(async (page: number = currentPage) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<SurchargeListResponse>(
        `/surcharges?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // MODIFIED: Added bypassCache for initial fetch
      );
      setSurcharges(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch surcharges: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching surcharges.');
      }
      setSurcharges([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchSurcharges(currentPage);
  }, [currentPage, fetchSurcharges]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createSurcharge = async (data: SurchargeCreate): Promise<boolean> => {
    setError(null);
    try {
      const payload = { ...data, percentage: parseFloat(data.percentage as any) };
      await apiClient<Surcharge>('/surcharges', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      await fetchSurcharges(1); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create surcharge: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateSurcharge = async (id: number, data: SurchargeUpdate): Promise<boolean> => {
    setError(null);
    try {
      const payload = { ...data };
      if (payload.percentage !== undefined) {
        payload.percentage = parseFloat(payload.percentage as any);
      }
      await apiClient<Surcharge>(`/surcharges/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchSurcharges(currentPage); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update surcharge: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteSurcharge = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Surcharge ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/surcharges/${id}`, { method: 'DELETE' });
      const pageToFetch = surcharges.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchSurcharges(pageToFetch); // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete surcharge: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    surcharges,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchSurcharges,
    handlePageChange,
    createSurcharge,
    updateSurcharge,
    deleteSurcharge,
  };
};